import { useQuery } from '@tanstack/react-query';

import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

import { getWalletBalancePath } from '../lib/wallet-api-path';
import type { WalletBalanceResponse } from '../model/types';

interface RawWalletBalanceResponse
  extends Omit<WalletBalanceResponse, 'walletBalance'> {
  walletBalance?: number | string | null;
  hasPendingAchWalletLoad?: boolean;
}

export const getWalletBalanceAPI = async (parentId: string, isV2 = false) => {
  const response = await baseAPI.get<RawWalletBalanceResponse | null>(
    getWalletBalancePath(parentId, isV2),
  );

  if (!response.data) return response.data;

  const walletBalance = Number(response.data.walletBalance ?? 0);

  return {
    ...response.data,
    walletBalance: Number.isFinite(walletBalance) ? walletBalance : 0,
    hasPendingAchWalletLoad: Boolean(response.data.hasPendingAchWalletLoad),
  };
};

const PENDING_ACH_POLL_INTERVAL_MS = 15_000;

export const useGetWalletBalance = (parentId: string | undefined) => {
  const { data: studioDetails } = useGetStudioDetails();
  const isV2 = studioDetails?.isV2 === true;

  return useQuery({
    queryKey: [KEYS.WALLET_BALANCE, parentId, isV2],
    queryFn: () => getWalletBalanceAPI(parentId!, isV2),
    enabled: Boolean(parentId),
    refetchInterval: (query) =>
      query.state.data?.hasPendingAchWalletLoad
        ? PENDING_ACH_POLL_INTERVAL_MS
        : false,
  });
};
